/**
 * 听写节奏（`dictation_session.tempo`）—— 类型 + 取值边界 + 默认值，**全项目只有这一份**。
 *
 * ## 为什么默认值必须是一个共享常量
 *
 * 这个对象同时被三处使用，而它们的口径必须逐字一致：
 *   1. **数据库 CHECK**（`ck_dictation_session_tempo_shape` 钉住取值范围）——
 *      它写在 SQL 里，只能靠人来对齐；
 *   2. **API 的 zod 校验**（`dictationTempoSchema` 的默认值与上下限）；
 *   3. **界面的初值**（家长打开"布置听写"时表单里预填什么）。
 *
 * 三处各写一份 `{ repeatCount: 2, gapSeconds: 5, betweenWordsSeconds: 12 }`，
 * 迟早会漂移成"表单预填 2 遍、后端只允许 1~3 遍"这种要试错才发现的错。
 * 所以常量与上下限都放这里，另两处引用它（SQL 那份只能靠注释与测试钉住，
 * 见 `packages/db/src/schema.ts` 的 `ck_dictation_session_tempo_shape` 与
 * `dictation.tempo.test.ts` 的越界用例）。
 *
 * ## 三个字段各自是什么（这是听写能不能用的关键）
 *
 * | 字段 | 含义 |
 * | --- | --- |
 * | `repeatCount` | **每个词读几遍**。默认 2（第一遍写、第二遍检查）。 |
 * | `gapSeconds` | **同一词两遍之间**等几秒，以及**每个词的最后一篇读完之后**再等几秒。 |
 * | `betweenWordsSeconds` | **两个词之间额外**再等几秒（叠加在 `gapSeconds` 之上）。 |
 *
 * 默认值合起来的效果：孩子听到"秋天"→ 等 5 秒 → 再听"秋天"→ 等 5+12 秒 → 下一个词。
 * 于是**每个词之间大约有 17 秒书写时间**，而"同一个词报两遍"之间只隔 5 秒
 * —— 这是家长实测里能用的节奏；嫌慢就把 `betweenWordsSeconds` 调小。
 *
 * ⚠️ 别把 `gapSeconds` 理解成"词与词之间的全部间隔"：那样"同词两遍之间的间隔"
 *   就没有地方放了（它比词间间隔短得多，不能共用一个值）。
 */
import { z } from 'zod';

/** 听写节奏。 */
export interface DictationTempoConfig {
  /** 每个词读几遍。 */
  repeatCount: number;
  /** 同一词两遍之间、以及每个词读完之后各等几秒。 */
  gapSeconds: number;
  /** 两个词之间**额外**再等几秒。 */
  betweenWordsSeconds: number;
}

/** 取值范围（**全项目唯一出处**；DB 的 CHECK 与这里同口径）。 */
export const DICTATION_TEMPO_LIMITS = {
  repeatCount: { min: 1, max: 5 },
  gapSeconds: { min: 0, max: 15 },
  betweenWordsSeconds: { min: 0, max: 60 },
} as const;

/**
 * 新建听写会话时的默认节奏。
 *
 * 选了"读 2 遍"而不是 1 遍：只听一遍的孩子常常在"这个字怎么写"上卡住，
 * 而第二遍是家长**不用起身**就能补救的那一次。
 */
export const DEFAULT_DICTATION_TEMPO: DictationTempoConfig = {
  repeatCount: 2,
  gapSeconds: 5,
  betweenWordsSeconds: 12,
};

/** `dictation_session.tempo` 的校验（三个字段都必填，不给默认，避免形状半边齐全）。 */
export const dictationTempoSchema = z.object({
  repeatCount: z
    .number()
    .int()
    .min(DICTATION_TEMPO_LIMITS.repeatCount.min)
    .max(DICTATION_TEMPO_LIMITS.repeatCount.max),
  gapSeconds: z
    .number()
    .int()
    .min(DICTATION_TEMPO_LIMITS.gapSeconds.min)
    .max(DICTATION_TEMPO_LIMITS.gapSeconds.max),
  betweenWordsSeconds: z
    .number()
    .int()
    .min(DICTATION_TEMPO_LIMITS.betweenWordsSeconds.min)
    .max(DICTATION_TEMPO_LIMITS.betweenWordsSeconds.max),
});

/** 传进来的节奏补齐成完整对象（缺项用默认值）；交给 zod 之前用。 */
export function withTempoDefaults(partial?: Partial<DictationTempoConfig> | null): DictationTempoConfig {
  return {
    repeatCount: partial?.repeatCount ?? DEFAULT_DICTATION_TEMPO.repeatCount,
    gapSeconds: partial?.gapSeconds ?? DEFAULT_DICTATION_TEMPO.gapSeconds,
    betweenWordsSeconds:
      partial?.betweenWordsSeconds ?? DEFAULT_DICTATION_TEMPO.betweenWordsSeconds,
  };
}

/**
 * 一次听写的**预计总时长**（秒）＝ 各题的朗读时长 + 各种间隔。
 *
 * 公式（`n` = 词数）：
 *   `Σ每词时长 + n·(repeatCount-1)·gapSeconds + n·gapSeconds + (n-1)·betweenWordsSeconds`
 *
 * 拿它做"开始前先告诉家长大约要多久"这一件事。真实时长会被实际音频长度带偏
 * （录音比 TTS 慢或快），所以界面必须写「约」。
 *
 * 每个词的基础时长：优先用**家长录音的真实毫秒数**（`durationMs` 拿不到就按
 * 每字 0.45 秒 + 0.8 秒估算 —— TTS 的语速大致如此，宁可估长一点）。
 */
export function estimateDictationSeconds(
  wordCount: number,
  tempo: DictationTempoConfig,
  options: { spokenMs?: readonly (number | null)[] } = {},
): number {
  if (!Number.isFinite(wordCount) || wordCount <= 0) return 0;
  const n = Math.floor(wordCount);
  const spoken: readonly (number | null)[] = options.spokenMs ?? [];
  let totalMs = 0;
  for (let i = 0; i < n; i += 1) {
    const ms = spoken[i];
    if (typeof ms === 'number' && Number.isFinite(ms) && ms > 0) totalMs += ms;
    else totalMs += DEFAULT_SPOKEN_MS_PER_WORD;
  }
  totalMs += n * Math.max(0, tempo.repeatCount - 1) * tempo.gapSeconds * 1000;
  totalMs += n * tempo.gapSeconds * 1000;
  totalMs += Math.max(0, n - 1) * tempo.betweenWordsSeconds * 1000;
  return Math.round(totalMs / 1000);
}

/**
 * 没有录音、也不知道 TTS 会读多久时，一个词的朗读时长估计。
 *
 * 取 2 秒（约 4 个字的词）：宁可把总时长估长一点 —— 家长按"约 6 分钟"安排，
 * 实际 5 分钟结束是可以接受的；反过来会让人以为程序卡住了。
 */
export const DEFAULT_SPOKEN_MS_PER_WORD = 2000;

/** 「约 5 分 20 秒」/「约 40 秒」——刻意不复用 `formatDuration`（那是**实际用时**的口径）。 */
export function formatApproxDuration(seconds: number | null | undefined): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return '—';
  const total = Math.round(seconds);
  if (total < 60) return `约 ${total} 秒`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  return s === 0 ? `约 ${m} 分钟` : `约 ${m} 分 ${s} 秒`;
}
